import { useIntl } from 'react-intl';
import { Button, Divider, Form, Modal, Skeleton } from 'antd';
import { useEffect, useState } from 'react';

import { PageProps } from '@/types/PageProps';

import TabContentWrapper from '@/components/_common/TabContentWrapper';
import { SaveButton } from '@/components/_common/Buttons';
import ForecastPayableForm from '@/components/Registry/BillToPay/ForecastPayableForm';
import {
  useForecastPayable,
  useForecastPayableCreateAccountPayable,
  useForecastPayableDelete,
  useForecastPayableUpdate,
} from '@/core/domain/financial/ForecastPayable/ForecastPayable.hooks';
import {
  Creditor,
  ForecastPayableFormValues,
  ForecastPayableGenerateAccountRequest,
  ForecastPayableUpdateRequest,
  GenerateAccountPayableFormValues,
} from '@/core/domain/financial/ForecastPayable/ForecastPayable.types';
import { useHistory, useRouteMatch } from 'react-router-dom';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useDisclosure } from '@/hooks';
import GenerateAccountPayableModal from '@/components/Registry/BillToPay/ForecastPayableForm/GenerateAccountPayableModal';
import { DateTimeFormat } from '@/utils/moment';
import ButtonBack from '@/components/_common/ButtonBack';
import handleFormChange from '@/hooks/handleFormChange';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

function ForecastPayableDetailsPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<ForecastPayableFormValues>();
  const history = useHistory();
  const [modalGenerate, modalGenerateAction] = useDisclosure();
  const [formChange, setFormChange] = useState({});
  const disabled = !Object.keys(formChange).some((key) => !!key);

  const { forecastPayableId } = useRouteMatch<{
    forecastPayableId: string;
  }>().params;

  const { data: forecastPayableData, isFetching } = useForecastPayable(
    Number(forecastPayableId),
  );

  useEffect(() => {
    if (forecastPayableData) {
      form.setFieldsValue({
        ...forecastPayableData,
        supplierId: forecastPayableData.supplier.id,
        employeeId: forecastPayableData.employee.id,
        supplier: forecastPayableData.supplier.name,
        employee: forecastPayableData.employee.name,
        creditor: forecastPayableData.employee.id
          ? Creditor.employee
          : Creditor.supplier,
      });
    }
  }, [forecastPayableData]);

  const updateMutation = useForecastPayableUpdate();
  const removeMutation = useForecastPayableDelete();
  const generateAccountMutation = useForecastPayableCreateAccountPayable();

  const handleUpdate = async () => {
    const values = await form.validateFields();

    const obj: ForecastPayableUpdateRequest = {
      ...values,
      id: Number(forecastPayableId),
      employeeId: values.employeeId ?? undefined,
      supplierId: values.supplierId ?? undefined,
    };

    await updateMutation.mutateAsync(obj);
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Remoção da previsão',
      content:
        'Deseja realmente remover a previsão, essa ação será irreversível',
      onOk: async () => {
        await removeMutation.mutateAsync(Number(forecastPayableId));
        history.push('/app/financial/billToPay/forecastPayable');
      },
      okType: 'danger',
    });
  };

  const handleGenerateAccount = async (
    values: GenerateAccountPayableFormValues,
  ) => {
    if (values && forecastPayableData) {
      const obj: ForecastPayableGenerateAccountRequest = {
        forecastPayableId: forecastPayableData.id,
        initialDate: values.initialDate.format(DateTimeFormat),
        finalDate: values.finalDate.format(DateTimeFormat),
        issueDate: values.issueDate,
        periodDate: values.periodDate,
      };

      await generateAccountMutation.mutateAsync(obj);

      modalGenerateAction.close();
    }
  };

  return (
    <>
      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <SpaceForButton>
            <Button
              block
              danger
              onClick={handleDelete}
              loading={removeMutation.isLoading}
              disabled={
                !hasPermissions([
                  Authorities.FINANCIAL_ACCOUNT_PAYABLE_DELETE_FORECAST_OF_ACCOUNTS_PAYABLE,
                ])
              }
            >
              {intl.formatMessage({ id: 'commons.entity.remove' })}
            </Button>

            <SaveButton
              block
              type="primary"
              loading={updateMutation.isLoading}
              onClick={handleUpdate}
              disabled={
                !hasPermissions([
                  Authorities.FINANCIAL_ACCOUNT_PAYABLE_EDIT_FORECAST_OF_ACCOUNTS_PAYABLE,
                ])
              }
            >
              {intl.formatMessage({ id: 'commons.form.button.save' })}
            </SaveButton>
          </SpaceForButton>
        }
      >
        <Skeleton loading={isFetching} active>
          <Form.Provider
            onFormChange={(name, info) =>
              handleFormChange(name, info, setFormChange)
            }
          >
            <ForecastPayableForm form={form} />
          </Form.Provider>
        </Skeleton>

        <Divider />

        <SpaceForButton>
          <ButtonBack
            path="/app/financial/billToPay/forecastPayable"
            disabled={disabled}
          />

          <Button
            disabled={
              !hasPermissions([
                Authorities.FINANCIAL_ACCOUNT_PAYABLE_GENERATE_ACCOUNTS_PAYABLE,
              ])
            }
            type="primary"
            block
            onClick={modalGenerateAction.open}
          >
            Gerar Contas a Pagar
          </Button>
        </SpaceForButton>
      </TabContentWrapper>

      <GenerateAccountPayableModal
        open={modalGenerate}
        onCancel={modalGenerateAction.close}
        onCallback={handleGenerateAccount}
        isLoading={generateAccountMutation.isLoading}
      />
    </>
  );
}

export default ForecastPayableDetailsPage;
